import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Settings, PenLine, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["company_name", "Company name"], ["phones", "Phone numbers"], ["email", "Email"], ["website", "Website"],
  ["address", "Address", true], ["gst_no", "GST No."], ["pan_no", "PAN No."], ["branch_state", "Branch State"],
  ["sac_code", "Services Accounting Code"], ["terms", "Terms and Conditions", true], ["bank_name", "Bank Name"],
  ["bank_ac_no", "Bank AC No."], ["bank_branch", "Branch (new line = second line)", true], ["ifsc", "IFSC Code"],
  ["account_type", "Account Type"],
];

const SignatureSection = ({ open }) => {
  const { user } = useAuth();
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const isAdmin = user?.role === "admin";
  useEffect(() => { if (open) api.get("/settings/signature").then((r) => setSig(r.data.data_url || "")).catch(() => {}); }, [open]);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/settings/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSig(data.data_url); toast.success("Signature uploaded");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async () => {
    if (!window.confirm("Remove the saved signature?")) return;
    try { await api.delete("/settings/signature"); setSig(""); toast.success("Signature removed"); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="border border-border p-3 space-y-2" data-testid="signature-settings">
      <div className="flex items-center gap-2 font-heading font-bold uppercase text-sm"><PenLine className="h-4 w-4 text-primary" /> Signature</div>
      <p className="text-xs text-muted-foreground">Upload your digital signature (PNG with a transparent background works best). It prints above "Authorised Signatory" when "Print with Signature" is selected.</p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-20 w-56 border border-dashed border-border bg-white flex items-center justify-center">
          {sig ? <img src={sig} alt="Signature" className="max-h-[72px] max-w-[210px] object-contain" data-testid="signature-preview" /> : <span className="text-xs text-neutral-400">No signature uploaded</span>}
        </div>
        {isAdmin ? (
          <div className="flex gap-2">
            <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="signature-file-input" />
            <Button variant="outline" className="rounded-none gap-1.5" onClick={() => ref.current?.click()} disabled={busy} data-testid="signature-upload-btn"><Upload className="h-4 w-4" /> {busy ? "Uploading…" : sig ? "Replace" : "Upload"}</Button>
            {sig && <Button variant="ghost" className="rounded-none gap-1.5 text-destructive" onClick={remove} data-testid="signature-remove-btn"><Trash2 className="h-4 w-4" /> Remove</Button>}
          </div>
        ) : <span className="text-xs text-muted-foreground">Only admins can change the signature.</span>}
      </div>
    </div>
  );
};

export function InvoiceSettingsDialog({ open, onClose }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(null);
    api.get("/settings/company").then((r) => setF(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/company", f); toast.success("Invoice settings saved"); onClose(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invoice-settings-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2"><Settings className="h-5 w-5 text-primary" /> Invoice Settings</DialogTitle></DialogHeader>
        <SignatureSection open={open} />
        {!f ? <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {FIELDS.map(([k, label, multi]) => (
              <div key={k} className={multi ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{label}</Label>
                {multi
                  ? <Textarea rows={2} className="rounded-none mt-1 text-sm" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`inv-set-${k}`} />
                  : <Input className="rounded-none mt-1 h-9" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`inv-set-${k}`} />}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none font-bold uppercase tracking-wide" onClick={save} disabled={!f || saving} data-testid="inv-settings-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
